const _ = require('lodash');
const mapping = require('arbitrip-common/theme/mapping');
const { DomainMatchedThemeError } = require('./theme_error');
const { COMPANY_NAME } = require('arbitrip-common/theme/constants');
const ElalThemeConfig = require('./configs/Elal.json');
const DEFAULT_THEME_CONFIG_NAME = 'default'; //default arbitrip theme colors config
const THEME_IMPORT_MAX_ATTEMPTS = 3;
const THEME_IMPORT_RETRY_DELAY_MS = 150;

// El Al's theme is preloaded (bundled) rather than lazily imported.
// It is required not to see "Oops" screen flickering once mobile rendered in WebView.
const PRELOADED_THEME_CONFIGS = {
    [COMPANY_NAME.ELAL]: ElalThemeConfig,
};

const importThemeConfigWithRetry = async (name) => {
    if (PRELOADED_THEME_CONFIGS[name]) {
        return { default: PRELOADED_THEME_CONFIGS[name] };
    }
    let lastError;
    for (let attempt = 1; attempt <= THEME_IMPORT_MAX_ATTEMPTS; attempt++) {
        try {
            return await import(`arbitrip-common/theme/colors/configs/${name}.json`);
        } catch (error) {
            lastError = error;
            if (attempt < THEME_IMPORT_MAX_ATTEMPTS) {
                await new Promise((resolve) => setTimeout(resolve, THEME_IMPORT_RETRY_DELAY_MS));
            }
        }
    }
    throw lastError;
};

export const loadThemeConfig = async (company_id) => {
    let company_theme_name = DEFAULT_THEME_CONFIG_NAME;
    let isDomainMatched = false;

    // Check hostname first (if available in browser context)
    const hostname = mapping.getHostname();

    if (hostname) {
        const companyNameFromDomain = mapping.getCompanyNameFromDomain(hostname);
        if (companyNameFromDomain) {
            // Domain match found - use strict domain-based theme (ignore company_id)
            company_theme_name = companyNameFromDomain;
            isDomainMatched = true;
        }
    }

    // Fall back to company_id-based theme if no domain match or no hostname available
    if (company_theme_name === DEFAULT_THEME_CONFIG_NAME && company_id) {
        company_theme_name = _.get(mapping, company_id, DEFAULT_THEME_CONFIG_NAME);
    }

    let themeConfigModule;
    try {
        themeConfigModule = await importThemeConfigWithRetry(company_theme_name);
    } catch (error) {
        console.error('Error loading theme configuration:', error);

        // If theme was determined by domain match, don't silently fall back to default
        // This prevents showing wrong theme (e.g., Arbitrip) on company-specific domains
        if (isDomainMatched) {
            console.error(
                `Failed to load theme '${company_theme_name}' required by domain '${hostname}'. ` +
                    `This is a critical error - domain-matched themes must not fall back to default.`,
            );
            // Re-throw the error to prevent silent fallback
            throw new DomainMatchedThemeError(company_theme_name, hostname, error);
        }

        // Only fall back to default if theme was determined by company_id
        themeConfigModule = await importThemeConfigWithRetry(DEFAULT_THEME_CONFIG_NAME);
    }

    return themeConfigModule.default;
};

// Flattens config file and sets css variables
export const setCssVariables = (themeConfig, prefix = '') => {
    Object.entries(themeConfig).forEach(([key, value]) => {
        // Skip fonts section - it's handled separately
        if (key === 'fonts') {
            return;
        }
        if (typeof value === 'object' && value !== null) {
            // Recursive call for nested objects
            setCssVariables(value, `${prefix}${key}-`);
        } else {
            // Handle flat key-value pairs
            const cssVariableName = `--${prefix}${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
            document.documentElement.style.setProperty(cssVariableName, value);
        }
    });
};

// Loads custom fonts from theme config and injects @font-face declarations
export const loadThemeFonts = (themeConfig) => {
    // Remove any existing theme font stylesheet
    const existingStyle = document.getElementById('theme-fonts');
    if (existingStyle) {
        existingStyle.remove();
    }

    // If no fonts config, use fallback fonts
    if (!themeConfig?.fonts || !themeConfig.fonts.family || !themeConfig.fonts.files) {
        // Set CSS variables for fallback fonts
        document.documentElement.style.setProperty(
            '--theme-font-family',
            'Lato, Raleway, Open Sans, Roboto, sans-serif',
        );
        document.documentElement.style.setProperty('--theme-main-font', 'Raleway');
        document.documentElement.style.setProperty('--theme-secondary-font', 'Lato');
        document.documentElement.style.setProperty('--theme-clean-font', 'Open Sans');
        return;
    }

    const { family, files } = themeConfig.fonts;

    // Generate @font-face declarations
    let fontFaceCSS = '';
    files.forEach((fontFile) => {
        const { path, weight = 400, style = 'normal' } = fontFile;
        fontFaceCSS += `
@font-face {
	font-family: '${family}';
	src: url('${path}') format('truetype');
	font-weight: ${weight};
	font-style: ${style};
	font-display: swap;
}
`;
    });

    // Inject CSS into document head
    const style = document.createElement('style');
    style.id = 'theme-fonts';
    style.textContent = fontFaceCSS;
    document.head.appendChild(style);

    // Set CSS variables for font family
    const fallbackFonts = 'Lato, Raleway, Open Sans, Roboto, sans-serif';
    document.documentElement.style.setProperty('--theme-font-family', `'${family}', ${fallbackFonts}`);
    document.documentElement.style.setProperty('--theme-main-font', `'${family}'`);
    document.documentElement.style.setProperty('--theme-secondary-font', `'${family}'`);
    document.documentElement.style.setProperty('--theme-clean-font', `'${family}'`);
};

const toCamelCase = (str) => {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

// Helper function to recursively transform object keys to camelCase
export const transformKeys = (obj) => {
    if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce(
            (acc, key) => {
                const camelCaseKey = toCamelCase(key);
                acc[camelCaseKey] = transformKeys(obj[key]); // Recursive call for nested objects
                return acc;
            },
            Array.isArray(obj) ? [] : {},
        );
    }
    return obj;
};
